import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RotateCcw, Plus, X, Loader2, RefreshCw,
  Search, Filter, ChevronDown, ChevronRight, ArrowRight,
} from 'lucide-react';
import { api } from '@/utils/api';
import { ImageUploader } from '@/components/shared/ImageUploader';

// ─── Types ───────────────────────────────────────────────────────────────────

interface VendorReturnItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  unit: string;
  rate: number;
  total: number;
  reason: string;
  qcPhotos?: string[];
}

interface VendorReturn {
  id: string;
  number: string;
  vendorId: string;
  vendorName: string;
  type: string;
  status: string;
  totalAmount: number;
  notes: string | null;
  debitNoteId: string | null;
  debitNoteNumber: string | null;
  replacementPOId: string | null;
  qcPhotos?: string[];
  items: VendorReturnItem[];
  createdAt: string;
  updatedAt: string;
}

interface Vendor {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  unit: string;
}

interface NewReturnItem {
  productId: string;
  productName: string;
  quantity: number;
  unit: string;
  reason: string;
  qcPhotos: string[];
}

// ─── Status Config ───────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  initiated: 'bg-yellow-500/20 text-yellow-400',
  approved: 'bg-blue-500/20 text-blue-400',
  dispatched: 'bg-purple-500/20 text-purple-400',
  received_by_vendor: 'bg-indigo-500/20 text-indigo-400',
  closed: 'bg-emerald-500/20 text-emerald-400',
  cancelled: 'bg-red-500/20 text-red-400',
  // Legacy statuses (backward compat)
  draft: 'bg-gray-500/20 text-gray-400',
  pending_approval: 'bg-yellow-500/20 text-yellow-400',
  in_transit: 'bg-purple-500/20 text-purple-400',
  credit_received: 'bg-emerald-500/20 text-emerald-400',
  replacement_ordered: 'bg-cyan-500/20 text-cyan-400',
};

const STATUS_FLOW: Record<string, { label: string; next: string | null; nextLabel: string | null }> = {
  initiated: { label: 'Initiated', next: 'approved', nextLabel: 'Approve' },
  approved: { label: 'Approved', next: 'dispatched', nextLabel: 'Mark Dispatched' },
  dispatched: { label: 'Dispatched', next: 'received_by_vendor', nextLabel: 'Received by Vendor' },
  received_by_vendor: { label: 'Received by Vendor', next: 'closed', nextLabel: 'Close' },
  closed: { label: 'Closed', next: null, nextLabel: null },
  cancelled: { label: 'Cancelled', next: null, nextLabel: null },
  // Legacy
  draft: { label: 'Draft', next: 'approved', nextLabel: 'Approve' },
  pending_approval: { label: 'Pending Approval', next: 'approved', nextLabel: 'Approve' },
  in_transit: { label: 'In Transit', next: 'received_by_vendor', nextLabel: 'Received by Vendor' },
  credit_received: { label: 'Credit Received', next: 'closed', nextLabel: 'Close' },
  replacement_ordered: { label: 'Replacement Ordered', next: 'closed', nextLabel: 'Close' },
};

const STATUS_OPTIONS = [
  'initiated', 'approved', 'dispatched', 'received_by_vendor', 'closed', 'cancelled',
];

const REASON_OPTIONS = [
  { value: 'defective', label: 'Defective Material' },
  { value: 'damaged', label: 'Damaged During Transport' },
  { value: 'wrong_item', label: 'Wrong Item Supplied' },
  { value: 'quality_failure', label: 'Quality Failure (QC Reject)' },
  { value: 'expired', label: 'Expired / Near Expiry' },
  { value: 'excess', label: 'Excess Quantity' },
  { value: 'specification_mismatch', label: 'Specification Mismatch' },
];

function StatusBadge({ status }: { status: string }) {
  const flow = STATUS_FLOW[status];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {flow?.label || status.replace(/_/g, ' ')}
    </span>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function VendorReturnsPage() {
  const navigate = useNavigate();
  const [returns, setReturns] = useState<VendorReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Expanded row
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // New Return Modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [newVendorId, setNewVendorId] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newItems, setNewItems] = useState<NewReturnItem[]>([
    { productId: '', productName: '', quantity: 1, unit: 'pcs', reason: 'defective', qcPhotos: [] },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (vendorSearch) params.search = vendorSearch;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const { data } = await api.get('/vendor-returns', { params });
      setReturns(data.data || []);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load vendor returns');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, vendorSearch, dateFrom, dateTo]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const openNewModal = async () => {
    setShowNewModal(true);
    try {
      const [vendorRes, productRes] = await Promise.all([
        api.get('/vendors', { params: { limit: 200 } }),
        api.get('/products', { params: { limit: 500 } }),
      ]);
      setVendors((vendorRes.data.data?.vendors ?? vendorRes.data.data) || []);
      setProducts((productRes.data.data?.products ?? productRes.data.data) || []);
    } catch { /* silent */ }
  };

  const addItemRow = () => {
    setNewItems([...newItems, { productId: '', productName: '', quantity: 1, unit: 'pcs', reason: 'defective', qcPhotos: [] }]);
  };

  const updateItem = (idx: number, field: keyof NewReturnItem, value: any) => {
    const updated = [...newItems];
    (updated[idx] as any)[field] = value;
    if (field === 'productId') {
      const p = products.find((pr) => pr.id === value);
      if (p) {
        updated[idx].productName = p.name;
        updated[idx].unit = p.unit || 'pcs';
      }
    }
    setNewItems(updated);
  };

  const removeItem = (idx: number) => {
    if (newItems.length === 1) return;
    setNewItems(newItems.filter((_, i) => i !== idx));
  };

  const submitReturn = async () => {
    if (!newVendorId) { alert('Select a vendor'); return; }
    if (newItems.some((i) => !i.productId || i.quantity <= 0)) { alert('Fill all item rows (product + quantity)'); return; }
    setSubmitting(true);
    try {
      const selectedVendor = vendors.find((v) => v.id === newVendorId);
      await api.post('/vendor-returns', {
        vendorId: newVendorId,
        vendorName: selectedVendor?.name || '',
        type: newItems[0]?.reason || 'defective',
        notes: newNotes || null,
        items: newItems.map((i) => ({
          productId: i.productId,
          productName: i.productName,
          quantity: i.quantity,
          unit: i.unit,
          rate: 0,
          reason: i.reason,
          qcPhotos: i.qcPhotos,
        })),
      });
      setShowNewModal(false);
      resetNewForm();
      await fetchReturns();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to create vendor return');
    } finally {
      setSubmitting(false);
    }
  };

  const resetNewForm = () => {
    setNewVendorId('');
    setNewNotes('');
    setNewItems([{ productId: '', productName: '', quantity: 1, unit: 'pcs', reason: 'defective', qcPhotos: [] }]);
  };

  // Status action
  const advanceStatus = async (vr: VendorReturn) => {
    const flow = STATUS_FLOW[vr.status];
    if (!flow?.next) return;
    setActionLoading(true);
    try {
      await api.patch(`/vendor-returns/${vr.id}/status`, { status: flow.next });
      await fetchReturns();
    } catch (e: any) {
      alert(e?.response?.data?.error || `Failed to update status`);
    } finally {
      setActionLoading(false);
    }
  };

  // Stats
  const countByStatus = (s: string) => returns.filter((r) => r.status === s).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--text-primary)]">
            <RotateCcw className="h-6 w-6 text-[var(--ink-0)]" />
            Purchase Returns
          </h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">Manage vendor returns — defective, damaged, wrong items</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchReturns}
            className="flex items-center gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
          >
            <RefreshCw size={16} />
          </button>
          <button
            onClick={openNewModal}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
          >
            <Plus className="h-4 w-4" /> New Return
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex flex-wrap gap-3">
        {(['initiated', 'approved', 'dispatched', 'received_by_vendor', 'closed'] as const).map((s) => (
          <div key={s} className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-4 py-2">
            <p className="text-xs text-[var(--text-muted)] capitalize">{STATUS_FLOW[s]?.label || s.replace(/_/g, ' ')}</p>
            <p className="text-lg font-bold text-[var(--text-primary)]">{countByStatus(s)}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search vendor..."
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] py-2 pl-9 pr-4 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)] dark:bg-[var(--ink-0)]/20 dark:text-[var(--saffron-600)]' : 'border-[var(--border-primary)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)]"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_FLOW[s]?.label || s.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)]" />
          <span className="text-xs text-[var(--text-muted)]">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)]" />
          {(statusFilter || dateFrom || dateTo) && (
            <button onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); }}
              className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]">
              <X size={12} /> Clear
            </button>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[var(--saffron-600)]" /></div>
        ) : returns.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--text-muted)]">No vendor returns found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-surface-hover)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3">Return #</th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Product(s)</th>
                  <th className="px-4 py-3 text-right">Qty</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {returns.map((vr) => {
                  const flow = STATUS_FLOW[vr.status];
                  return (
                    <>
                      <tr
                        key={vr.id}
                        className="hover:bg-[var(--bg-surface-hover)] cursor-pointer transition-colors"
                        onClick={() => setExpandedId(expandedId === vr.id ? null : vr.id)}
                      >
                        <td className="px-4 py-3">
                          {expandedId === vr.id ? <ChevronDown size={14} className="text-[var(--text-muted)]" /> : <ChevronRight size={14} className="text-[var(--text-muted)]" />}
                        </td>
                        <td className="px-4 py-3 font-medium text-[var(--ink-0)]">{vr.number}</td>
                        <td className="px-4 py-3 text-[var(--text-primary)]">{vr.vendorName}</td>
                        <td className="px-4 py-3 text-[var(--text-secondary)] max-w-[200px] truncate">
                          {vr.items?.map((i) => i.productName).join(', ') || '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--text-primary)]">
                          {vr.items?.reduce((sum, i) => sum + i.quantity, 0) || 0}
                        </td>
                        <td className="px-4 py-3 text-[var(--text-secondary)] capitalize text-xs">
                          {vr.type?.replace(/_/g, ' ') || '-'}
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={vr.status} /></td>
                        <td className="px-4 py-3 text-[var(--text-muted)] text-xs">
                          {new Date(vr.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          {flow?.next && (
                            <button
                              onClick={() => advanceStatus(vr)}
                              disabled={actionLoading}
                              className="inline-flex items-center gap-1 rounded-lg bg-[var(--ink-0)] px-2.5 py-1 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                            >
                              {actionLoading ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                              {flow.nextLabel}
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* Expanded Detail */}
                      {expandedId === vr.id && (
                        <tr key={`${vr.id}-detail`}>
                          <td colSpan={9} className="bg-[var(--bg-surface-hover)]/50 px-5 py-4">
                            <div className="space-y-3">
                              {/* Items */}
                              <div>
                                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Return Items</h4>
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-xs text-[var(--text-muted)]">
                                      <th className="pb-1">Product</th>
                                      <th className="pb-1">SKU</th>
                                      <th className="pb-1 text-right">Qty</th>
                                      <th className="pb-1">Unit</th>
                                      <th className="pb-1">Reason</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {vr.items.map((item) => (
                                      <tr key={item.id} className="border-t border-[var(--border-primary)]/50">
                                        <td className="py-1.5 font-medium text-[var(--text-primary)]">{item.productName}</td>
                                        <td className="py-1.5 text-[var(--text-muted)] font-mono text-xs">{item.sku}</td>
                                        <td className="py-1.5 text-right text-[var(--text-primary)]">{item.quantity}</td>
                                        <td className="py-1.5 text-[var(--text-muted)]">{item.unit}</td>
                                        <td className="py-1.5 text-[var(--text-secondary)] capitalize text-xs">{item.reason?.replace(/_/g, ' ') || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Debit Note */}
                              {vr.debitNoteNumber && (
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 dark:border-emerald-800 dark:bg-emerald-900/20">
                                  <span className="text-xs text-emerald-600 dark:text-emerald-400">Linked Debit Note:</span>{' '}
                                  <span className="font-medium text-emerald-800 dark:text-emerald-200">{vr.debitNoteNumber}</span>
                                </div>
                              )}

                              {/* Notes */}
                              {vr.notes && (
                                <div className="text-sm text-[var(--text-secondary)]">
                                  <span className="font-medium text-[var(--text-primary)]">Notes:</span> {vr.notes}
                                </div>
                              )}

                              {/* Status Flow Visual */}
                              <div className="flex items-center gap-2 text-xs">
                                {STATUS_OPTIONS.filter(s => s !== 'cancelled').map((s, i) => (
                                  <span key={s} className="flex items-center gap-1">
                                    {i > 0 && <ArrowRight size={10} className="text-[var(--text-muted)]" />}
                                    <span className={vr.status === s
                                      ? 'rounded-full bg-[var(--ink-0)] px-2 py-0.5 text-white font-medium'
                                      : STATUS_OPTIONS.indexOf(vr.status) > STATUS_OPTIONS.indexOf(s)
                                        ? 'text-emerald-500'
                                        : 'text-[var(--text-muted)]'
                                    }>
                                      {STATUS_FLOW[s]?.label || s}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── New Return Modal ── */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowNewModal(false)}>
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--border-primary)] bg-[var(--bg-surface)] px-6 py-4 rounded-t-xl">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">New Vendor Return</h2>
              <button onClick={() => { setShowNewModal(false); resetNewForm(); }} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5 px-6 py-5">
              {/* Vendor */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-[var(--text-secondary)]">Vendor *</label>
                  <select
                    value={newVendorId}
                    onChange={(e) => setNewVendorId(e.target.value)}
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
                  >
                    <option value="">Select vendor...</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-[var(--text-secondary)]">Notes</label>
                  <input
                    type="text"
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                    placeholder="Additional notes..."
                    className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
                  />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-medium text-[var(--text-secondary)]">Return Items</label>
                  <button onClick={addItemRow} className="text-xs font-medium text-[var(--ink-0)] hover:text-[var(--ink-0)]">+ Add Item</button>
                </div>
                <div className="space-y-4">
                  {newItems.map((item, idx) => (
                    <div key={idx} className="rounded-lg border border-[var(--border-primary)] p-4">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                        {/* Product */}
                        <div className="sm:col-span-2">
                          <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Product *</label>
                          <select
                            value={item.productId}
                            onChange={(e) => updateItem(idx, 'productId', e.target.value)}
                            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)]"
                          >
                            <option value="">Select...</option>
                            {products.map((p) => (
                              <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                            ))}
                          </select>
                        </div>
                        {/* Qty */}
                        <div>
                          <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Qty *</label>
                          <input
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))}
                            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)]"
                          />
                        </div>
                        {/* Reason */}
                        <div>
                          <label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">Reason *</label>
                          <select
                            value={item.reason}
                            onChange={(e) => updateItem(idx, 'reason', e.target.value)}
                            className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)]"
                          >
                            {REASON_OPTIONS.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* QC Fail Photos */}
                      <div className="mt-3">
                        <ImageUploader
                          images={item.qcPhotos}
                          onChange={(photos) => updateItem(idx, 'qcPhotos', photos)}
                          maxImages={10}
                          label="QC Fail Photos (attach evidence)"
                        />
                      </div>

                      {/* Remove button */}
                      {newItems.length > 1 && (
                        <div className="mt-2 text-right">
                          <button onClick={() => removeItem(idx)} className="text-xs text-red-500 hover:text-red-600">Remove item</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-[var(--border-primary)] px-6 py-4">
              <button
                onClick={() => { setShowNewModal(false); resetNewForm(); }}
                className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={submitReturn}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Return
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
